import { useState, type ReactNode } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useDismissiblePanel } from '@/contexts/UIPreferencesContext';

/**
 * First-contact concept explainer (task 539).
 *
 * A collapsible, dismissible panel placed on the surface where a renamed
 * concept is first met — what a capability is, what "what it needs from you /
 * what it gives back" mean, what Connect gives you. Collapsing is a
 * this-visit convenience; dismissing is permanent and rides the shared
 * UI-preference ledger (task 526), the same mechanics as the user-menu
 * preference toggles.
 *
 * The whole explainer set is gated here rather than at each call site, so one
 * flag turns all of them on and off.
 */

/** One flag for the explainer set. */
export const EXPLAINERS_FLAG = 'innovate.fe.help.explainers' as const;

interface ExplainerPanelProps {
  /** Stable preference key for this panel's per-User dismissal. */
  panelKey: string;
  /** Panel heading — the question the explainer answers. */
  title: string;
  /** Explainer body. */
  children: ReactNode;
  /** Test hook for the wrapper. Defaults to `explainer-panel`. */
  testId?: string;
  className?: string;
}

export default function ExplainerPanel({
  panelKey,
  title,
  children,
  testId = 'explainer-panel',
  className = '',
}: ExplainerPanelProps) {
  const explainersEnabled = useFlag(EXPLAINERS_FLAG);
  const { isDismissed, dismiss } = useDismissiblePanel(panelKey);
  const [collapsed, setCollapsed] = useState(false);

  if (!explainersEnabled || isDismissed) return null;

  return (
    <div
      data-testid={testId}
      className={`rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 ${className}`}
    >
      <div className="flex items-center gap-2 px-4 py-2.5">
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-expanded={!collapsed}
          data-testid={`${testId}-toggle`}
          className="flex flex-1 items-center gap-1 text-left text-sm font-semibold text-indigo-800 dark:text-indigo-200"
        >
          {title}
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4 text-indigo-500" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-indigo-500" />
          )}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={`Dismiss: ${title}`}
          data-testid={`${testId}-dismiss`}
          className="flex-shrink-0 text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      {!collapsed && (
        <div
          data-testid={`${testId}-body`}
          className="px-4 pb-3.5 pl-10 space-y-2 text-sm text-indigo-900 dark:text-indigo-100"
        >
          {children}
        </div>
      )}
    </div>
  );
}
